import { useState, useEffect } from "react";
import { Box, CardContent, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import Button from '@mui/material/Button';
import { auth } from "../../../../contexts/authentication";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { UserData, getUser } from "../../getUserDB";
import io from 'socket.io-client';
import createInstance from "../../../utils/instances/createInstance";
import instanceConnect from "../../../utils/instances/instanceConnect";
import fetchInstances from "../../../utils/instances/fetchInstances";
import desconnectInstance from "../../../utils/instances/desconnectInstance";
import deleteInstance from "../../../utils/instances/deleteInstance";
import LoadingCircular from "../../../components/LoadingCircular";
import LoadingLinear from "../../../components/LoadingLinear";
import LinearProgress from '@mui/material/LinearProgress';
import QrCode from "../../../components/dialogs/QrCode";

const baseUrl = import.meta.env.VITE_API_BASE_URL;

interface Props {
  setHideTabs: React.Dispatch<React.SetStateAction<boolean>>
}

export default function Instance({ setHideTabs }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [showQrCode, setShowQrCode] = useState(false);
  const [userPicture, setUserPicture] = useState<string | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [instanceWhatsapp, setInstanceWhatsapp] = useState<string | null>(null);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [btnCreateClicked, setBtnCreateClicked] = useState(false);
  const [showBtnConnect, setShowBtnConnect] = useState(true);
  const [loading, setLoading] = useState(true);
  const currentUser = auth.currentUser;
  const uid = currentUser?.uid;
  const [userDB, setUserDB] = useState<UserData>();

  useEffect(() => {
    if (userDB) return;
    const dataUser = async () => {
      const data = await getUser();
      setUserDB(data);
    }
    dataUser();
  }, [userDB]);

  useEffect(() => {
    const getInstance = async () => {
      const data = await fetchInstances();
      const index = data.find((item: { name: string }) => item.name === uid);

      if (index) {
        setLoading(false);
        setInstanceId(index.id);
        setUserStatus(index.connectionStatus);

        if (index.ownerJid) {
          setProfileName(index.profileName);
          setUserPicture(index.profilePicUrl);
          setInstanceWhatsapp(index.ownerJid.split('@')[0]);
        }
      } else {
        setLoading(false);
      }
    }
    const timer = setTimeout(() => {
      getInstance();
    }, 500);
    return () => clearTimeout(timer);
  }, [userStatus, userDB?.whatsapp]);

  useEffect(() => {
    const updateConnectState = () => {
      const socket = io(baseUrl, {
        query: { instanceId: uid },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000
      });
      try {
        socket.on('connect', () => {
          socket.emit('join-room', uid);
        });
        socket.on('connection-update', (state: string) => {
          setUserStatus(state);
        });
        socket.on('disconnect', () => {
        });
      } catch (error) {
        console.error('Erro ao atualizar estado de conexão:', error);
      }
      return () => {
        socket.disconnect();
      };
    }
    updateConnectState();

    if (userStatus === 'open') {
      setQrCode(null);
      setHideTabs(false);
      setShowQrCode(false);
    }
  }, [userStatus]);

  useEffect(() => {
    if (!showQrCode) return;
    return updatedQrCode();
  }, [showQrCode]);

  const updatedQrCode = () => {
    const socket = io(baseUrl, {
      query: { instanceId: uid },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000
    });
    try {      
      socket.on('connect', () => {
        socket.emit('join-room', uid);       
      });
      socket.on('qrcode-updated', (base64: string) => {        
        setQrCode(base64);
      });
      socket.on('disconnect', () => {
      });
    } catch (error) {
      console.error('Erro ao atualizar qrcode:', error);
    }
    return () => {
      socket.disconnect();
    };
  }

  const handleCreateInstance = async (e: any) => {
    try {
      setBtnCreateClicked(true);
      setQrCode('Carregando...');
      setShowQrCode(true);
      e.target.disabled = true;
      e.target.textContent = 'Criando...';
      const dataCreate = await createInstance(uid);
      setInstanceId(dataCreate.instance.instanceId);
      setQrCode(dataCreate.qrcode.base64);
      e.target.disabled = false;
    } catch (error) {
      console.error('Erro ao gerar o QrCode:', error);
    }
  }

  const handleConnectInstance = async (e: any) => {
    try {
      e.target.disabled = true;
      e.target.textContent = 'Gerando QRCode...';
      const response = await instanceConnect(uid);
      setQrCode(response.base64);
      setShowQrCode(true);
      !showQrCode && (e.target.disabled = false);
    } catch (error) {
      console.error('Erro ao conectar instância:', error);
    }
  }

  const handleDesconnectInstance = async (e: any) => {
    try {
      e.target.disabled = true;
      e.target.textContent = 'Desconectando...';
      await desconnectInstance(uid, userStatus);

    } catch (error) {
      console.error('Erro ao desconectar instância:', error);
    }
  }

  const handleDeleteInstance = async (e: any) => {
    try {
      e.target.disabled = true;
      e.target.textContent = 'Deletando...';
      setShowBtnConnect(false);
      userStatus === 'open' && await desconnectInstance(uid, userStatus);
      await deleteInstance(uid as any);
      const checkInstance = async () => {
        const data = await fetchInstances();
        const exists = data.some((item: any) => item.name === uid);
        if (!exists) {
          setInstanceId(null);
          setUserStatus(null);
          setProfileName(null);
          setUserPicture(null);
          setInstanceWhatsapp(null);
          setBtnCreateClicked(false);
          setShowBtnConnect(true);
          setHideTabs(true);
          e.target.disabled = false;
        } else {
          setTimeout(checkInstance, 2000);
        }
      }
      checkInstance();
    } catch (error) {
      console.error('Erro ao deletar instância:', error);
    }
  }

  return (
    <CardContent sx={{
      flexGrow: 1,
      display: 'flex',
      justifyContent: isDesktop ? 'unset' : 'center',
    }}
    >
      {loading && <LoadingLinear />}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        alignItems: 'center',
        maxWidth: 'max-content'
      }}>
        {!instanceId && !loading && !userStatus ? (
          <Box>
            <Button
              onClick={(e) => handleCreateInstance(e)}
              variant="contained"
              sx={{
                border: 'none !important',
              }}
            >
              Nova Conexão
            </Button>
          </Box>
        ) : (userStatus && (
          <Stack
            direction={isDesktop ? "row" : "column"}
            spacing={3}
            sx={{ display: 'flex', justifySelf: 'center' }}
          >
            <Box sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              minWidth: '200px',
              padding: '15px',
              // border: '1px solid'
            }}>
              <Box>
                <PowerSettingsNewIcon
                  fontSize={isMobile ? "medium" : "large"}
                  sx={{ color: 'text.secondary' }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Estado de conexão:
                </Typography>
                <Typography
                  gutterBottom
                  variant="h6"
                  component="div"
                  sx={{ marginBottom: 0 }}
                >
                  {userStatus === 'open' ? 'Conectado' : 'Desconectado'}
                </Typography>
              </Box>
            </Box>
            <Divider
              orientation={isDesktop ? "vertical" : "horizontal"}
              flexItem
            />
            {(profileName || instanceWhatsapp) && instanceId && !loading && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center',
                minWidth: '200px',
                padding: '15px'
              }}
              >
                {userPicture &&
                  <Box>
                    <img
                      src={userPicture}
                      alt="Profile"
                      style={{
                        width: '50px',
                        alignSelf: 'center',
                        borderRadius: '50%'
                      }}
                    />
                  </Box>
                }
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Usuário:
                  </Typography>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{ marginBottom: 0 }}
                  >
                    {profileName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    WhatsApp Conectado:
                  </Typography>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{ marginBottom: 0 }}
                  >
                    {instanceWhatsapp}
                  </Typography>
                </Box>
              </Box>
            )}
          </Stack>
        )
        )}
        {instanceId && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
            {!showQrCode && userStatus !== 'open' && (
              showBtnConnect &&
              <Box>
                <Button
                  variant="contained"
                  onClick={(e) => handleConnectInstance(e)}
                  sx={{
                    border: 'none !important',
                    minWidth: '120px'
                  }}
                >
                  Conectar
                </Button>
              </Box>
            )}
            <Box>
              <Button
                variant="outlined"
                onClick={(e) => handleDeleteInstance(e)}
                sx={{ minWidth: '120px' }}
              >
                Deletar
              </Button>
            </Box>
            {userStatus === 'open' && (
              <Box>
                <Button
                  onClick={(e) => handleDesconnectInstance(e)}
                  variant="outlined"
                >
                  Desconectar
                </Button>
              </Box>
            )}
          </Box>
        )}
        {showQrCode && qrCode && (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent={'space-between'}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <QrCode
                showQrCode={showQrCode}
                setShowQrCode={setShowQrCode}
                qrCode={qrCode}
              />
            </Box>
          </Stack>
        )}
      </Box>
    </CardContent>
  );
}